import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { startToolOperation, trackToolRejection, type ToolAnalyticsContext, type ToolErrorCode } from '@saas-pages/shared/public/runtime'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  calculateCombinatorics,
  MAX_COMBINATORICS_INPUT,
  MAX_COMBINATORICS_RESULT_DIGITS,
  type CombinatoricsCalculation,
  type CombinatoricsError,
  type CombinatoricsMode,
} from '@/pages/combinatorics-calculator/combinatorics-calculator'
import { useOnSettled, useSettledValue } from '@/shared/use-settled'
import { combinatoricsCalculatorRoute } from '@/shared/utility-registry'

export const COMBINATORICS_ANALYTICS_CONTEXT = {
  app: 'calcdeck',
  domain: 'calcdeck.ru',
  route: combinatoricsCalculatorRoute.path,
  utility: combinatoricsCalculatorRoute.analyticsKey,
  processingMode: 'local',
} satisfies ToolAnalyticsContext

interface ModeOption {
  key: CombinatoricsMode
  name: string
  order: string
  repetition: string
  formula: string
}

const modeOptions: ModeOption[] = [
  {
    key: 'ordered-without-repetition',
    name: 'Размещения без повторений',
    order: 'Порядок важен',
    repetition: 'Повторы запрещены',
    formula: 'A(n, k)',
  },
  {
    key: 'ordered-with-repetition',
    name: 'Размещения с повторениями',
    order: 'Порядок важен',
    repetition: 'Повторы разрешены',
    formula: 'nᵏ',
  },
  {
    key: 'unordered-without-repetition',
    name: 'Сочетания без повторений',
    order: 'Порядок не важен',
    repetition: 'Повторы запрещены',
    formula: 'C(n, k)',
  },
  {
    key: 'unordered-with-repetition',
    name: 'Сочетания с повторениями',
    order: 'Порядок не важен',
    repetition: 'Повторы разрешены',
    formula: 'C(n + k − 1, k)',
  },
]

export function CombinatoricsCalculatorPage() {
  const [mode, setMode] = useState<CombinatoricsMode>('unordered-without-repetition')
  const [n, setN] = useState('5')
  const [k, setK] = useState('3')
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle')

  const calculation = useMemo<CombinatoricsCalculation | null>(() => (
    n.trim() === '' || k.trim() === '' ? null : calculateCombinatorics(mode, n, k)
  ), [k, mode, n])

  useEffect(() => setCopyStatus('idle'), [calculation])
  useOnSettled(calculation, reportCombinatoricsCalculation)

  const updateInput = useCallback((setter: (value: string) => void) => (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setter(event.target.value)
  }, [])

  const reset = useCallback(() => {
    setMode('unordered-without-repetition')
    setN('5')
    setK('3')
  }, [])

  const copyResult = useCallback(async () => {
    if (calculation?.status !== 'success' || !navigator.clipboard) {
      setCopyStatus('error')
      return
    }

    try {
      await navigator.clipboard.writeText(calculation.result.value)
      setCopyStatus('copied')
    } catch {
      setCopyStatus('error')
    }
  }, [calculation])

  const liveError = calculation?.status === 'error' ? calculation.error : null
  const settledError = useSettledValue(liveError)
  const error = liveError === null ? null : settledError
  const result = calculation?.status === 'success' ? calculation.result : null

  return (
    <div className="mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-blue-700 via-indigo-500 to-sky-400" />
        <div className="grid min-w-0 lg:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.88fr)]">
          <div className="min-w-0 space-y-3 border-b p-4 sm:space-y-5 sm:p-6 lg:border-b-0 lg:border-r">
            <fieldset>
              <legend className="text-sm font-semibold">
                Важен ли порядок и разрешены ли повторения?
              </legend>
              <div className="mt-2 grid min-w-0 grid-cols-2 gap-2">
                {modeOptions.map((option) => (
                  <label
                    key={option.key}
                    className={cn(
                      'min-w-0 cursor-pointer rounded-md border p-2 transition-colors focus-within:ring-2 focus-within:ring-ring sm:p-3',
                      mode === option.key
                        ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                        : 'bg-background hover:border-primary/45',
                    )}
                  >
                    <input
                      type="radio"
                      name="combinatorics-mode"
                      value={option.key}
                      checked={mode === option.key}
                      onChange={() => setMode(option.key)}
                      className="sr-only"
                    />
                    <span className="block text-sm font-semibold">{option.name}</span>
                    <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                      {option.order} · {option.repetition}
                    </span>
                    <span className="mt-2 hidden font-mono text-xs sm:block">{option.formula}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="grid min-w-0 grid-cols-2 gap-3 sm:gap-4 sm:rounded-md sm:border sm:bg-background/70 sm:p-4 sm:shadow-inner">
              <NumberField
                id="combinatorics-n"
                label="n — всего"
                value={n}
                onChange={updateInput(setN)}
                invalid={error !== null}
              />
              <NumberField
                id="combinatorics-k"
                label="k — выбираем"
                value={k}
                onChange={updateInput(setK)}
                invalid={error !== null}
              />
              <p className="col-span-2 text-xs leading-5 text-muted-foreground">
                Целые числа от 0 до {MAX_COMBINATORICS_INPUT.toString()}. Не более пяти цифр в каждом поле.
              </p>
              {error && (
                <div
                  id="combinatorics-input-error"
                  role="alert"
                  className="col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {errorText(error)}
                </div>
              )}
            </div>
          </div>

          <div
            aria-live="polite"
            className="flex min-w-0 flex-col bg-background/55 p-4 sm:p-6 lg:min-h-[360px]"
          >
            <div className="text-sm font-medium text-muted-foreground">Точный результат</div>
            {result ? (
              <div className="mt-2 min-w-0 sm:mt-4">
                <div className="rounded-md border border-primary/35 bg-card p-4 shadow-sm">
                  <div className="text-sm font-semibold">{result.name}</div>
                  <div className="mt-3 max-h-80 overflow-auto rounded-md bg-background p-3">
                    <output className="block break-all font-mono text-2xl font-bold tabular-nums sm:text-3xl">
                      {result.value}
                    </output>
                  </div>
                </div>
                <div className="mt-3 min-w-0 rounded-md border bg-card p-4">
                  <div className="text-xs font-medium uppercase text-muted-foreground">Формула</div>
                  <div className="mt-2 break-words font-mono text-sm">{result.formula}</div>
                  <div className="mt-3 break-words font-mono text-sm leading-6 text-muted-foreground">
                    {result.substitution}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button variant="outline" onClick={copyResult}>Скопировать результат</Button>
                  <Button variant="ghost" onClick={reset}>Сбросить</Button>
                  <span role="status" className="text-sm text-primary">
                    {copyStatus === 'copied' ? 'Точное целое число скопировано.' : ''}
                  </span>
                  <span role="alert" className="text-sm text-destructive">
                    {copyStatus === 'error' ? 'Не удалось скопировать результат.' : ''}
                  </span>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {error ? 'Проверьте исходные данные.' : 'Укажите n и k: точное число появится здесь сразу.'}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <InfoCard
          title="Точная арифметика"
          text={`BigInt сохраняет все цифры ответа; результат ограничен ${MAX_COMBINATORICS_RESULT_DIGITS.toLocaleString('ru-RU')} цифрами.`}
        />
        <InfoCard
          title="Перестановка — частный случай"
          text="Когда повторы запрещены и k = n, размещение становится перестановкой всех n элементов."
        />
        <InfoCard
          title="Расчёт в браузере"
          text="Значения n, k и результат не отправляются на сервер. Калькулятор не строит и не перебирает сами наборы."
        />
      </section>

      <section className="mt-4 rounded-lg border bg-card p-4 text-sm leading-6 text-muted-foreground sm:p-5">
        <p>
          Команда «Скопировать результат» записывает точное число в системный буфер обмена.
          После этого к нему могут применяться правила браузера, операционной системы и синхронизации буфера.
        </p>
      </section>
    </div>
  )
}

function NumberField({
  id,
  label,
  value,
  onChange,
  invalid,
}: {
  id: string
  label: string
  value: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  invalid: boolean
}) {
  return (
    <label htmlFor={id} className="block min-w-0">
      <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
      <input
        id={id}
        inputMode="numeric"
        value={value}
        onChange={onChange}
        aria-invalid={invalid}
        aria-describedby={invalid ? 'combinatorics-input-error' : undefined}
        className={cn(
          'h-11 w-full min-w-0 rounded-md border bg-background px-3 font-mono text-base tabular-nums outline-none transition-colors',
          'focus:border-primary focus:ring-2 focus:ring-ring/20',
          invalid && 'border-destructive focus:border-destructive focus:ring-destructive/20',
        )}
      />
    </label>
  )
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return (
    <Card className="rounded-lg">
      <CardContent className="pt-6">
        <div className="font-semibold">{title}</div>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
      </CardContent>
    </Card>
  )
}

function errorText(error: CombinatoricsError): string {
  const messages: Record<CombinatoricsError, string> = {
    empty_input: 'Заполните оба поля: n и k.',
    invalid_integer: 'Введите неотрицательное целое число обычными цифрами, без знака, дробной части и экспоненты.',
    input_limit: `Каждое значение должно содержать не более пяти цифр и быть не больше ${MAX_COMBINATORICS_INPUT.toString()}.`,
    selection_exceeds_population: 'Без повторений нельзя выбрать больше элементов, чем есть: k должно быть не больше n.',
    size_limit: `Точный ответ превысит лимит ${MAX_COMBINATORICS_RESULT_DIGITS.toLocaleString('ru-RU')} цифр. Уменьшите n или k.`,
    processing_failed: 'Не удалось завершить точный расчёт. Измените значения и попробуйте ещё раз.',
  }
  return messages[error]
}

function reportCombinatoricsCalculation(calculation: CombinatoricsCalculation | null): void {
  if (!calculation) return
  if (calculation.status === 'success') {
    startToolOperation(COMBINATORICS_ANALYTICS_CONTEXT).success(0)
  } else if (calculation.error === 'processing_failed') {
    startToolOperation(COMBINATORICS_ANALYTICS_CONTEXT).error('processing_failed')
  } else {
    trackToolRejection(COMBINATORICS_ANALYTICS_CONTEXT, combinatoricsAnalyticsError(calculation.error))
  }
}

export function combinatoricsAnalyticsError(error: CombinatoricsError): ToolErrorCode {
  if (error === 'input_limit' || error === 'size_limit') return 'size_limit'
  if (error === 'processing_failed') return 'processing_failed'
  return 'invalid_input'
}
